import React, { useState, useEffect } from 'react';
import '../../styles/components/UsersList.scss';
import { Box, Typography, Button, Modal, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch } from '@mui/material';
import features from '../../config/features.js';

function UserModal({ keywords, templates, domainUserAttr, openModal, selectedUser, handleCloseModal, handleSaveUser }) {
  const [email, setEmail] = useState("");
  const [template, setTemplate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [dynamicInputs, setDynamicInputs] = useState([]);
  const [emailError, setEmailError] = useState(false);
  const [templateError, setTemplateError] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  
  // Estados para birthday y aniversary
  const [birthday, setBirthday] = useState("");
  const [aniversary, setAniversary] = useState("");

  useEffect(() => {
    if (selectedUser?.email) {
      setIsEmailValid(true)
      setEmail(selectedUser.email);
      setTemplate(selectedUser.signature_template_id);
      setIsActive(!!selectedUser.is_active);
      setIsNewUser(false);
    } else {
      setEmail("");
      setTemplate("");
      setIsActive(true);
      setIsNewUser(true);
    }
  }, [selectedUser]);

  useEffect(() => {
    const inputs = Array.isArray(keywords) ? keywords?.map(k => ({ ...k, value: domainUserAttr?.[k.name] || k.value })) : [];
    
    const formatDateForInput = (val) => {
      if (!val) return "";
      if (typeof val === 'string') return val;
      if (val._seconds !== undefined) {
        const d = new Date(val._seconds * 1000);
        return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : "";
      }
      if (val.seconds !== undefined) {
        const d = new Date(val.seconds * 1000);
        return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : "";
      }
      return "";
    };

    // Extraer birthday y aniversary de los inputs dinámicos
    const birthdayInput = inputs.find(i => i.name === 'birthday');
    const aniversaryInput = inputs.find(i => i.name === 'aniversary');

    setBirthday(formatDateForInput(birthdayInput?.value));
    setAniversary(formatDateForInput(aniversaryInput?.value));
    
    // Filtrar birthday y aniversary de los inputs dinámicos (no se mostrarán en el resto)
    const filteredInputs = inputs.filter(i => i.name !== 'birthday' && i.name !== 'aniversary');
    
    setDynamicInputs(filteredInputs);
  }, [keywords, domainUserAttr]);

  const handleInputChange = (name, value) => {
    setDynamicInputs((prev) =>
      Array.isArray(prev) ? prev.map((item) =>
        item.name === name ? { ...item, value } : item
      ) : []
    );
  };

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsEmailValid(validateEmail(value));
  };

  const handleEmailBlur = () => {
    setEmailError(!isEmailValid || !email);
  };

  const handleTemplateChange = (event) => setTemplate(event.target.value);
  const handleActiveChange = (event) => setIsActive(event.target.checked);
  
  // Manejadores para birthday y aniversary
  const handleBirthdayChange = (e) => setBirthday(e.target.value);
  const handleAniversaryChange = (e) => setAniversary(e.target.value);

  const onSave = () => {
    if (validateFields()) {
      const userData = { email, signature_template_id: template, is_active: isActive };
      
      // Agregar birthday y aniversary a los inputs dinámicos antes de guardar
      const finalInputs = [
        ...dynamicInputs,
        { name: 'birthday', value: birthday },
        { name: 'aniversary', value: aniversary }
      ];
      
      handleSaveUser(userData, finalInputs, isNewUser);
    }
  };

  const validateFields = () => {
    setEmailError(!isEmailValid || !email);
    setTemplateError(!template);
    return isEmailValid && email && template;
  };

  const handleLocalCloseModal = () => {
    setEmailError(false);
    setTemplateError(false);
    handleCloseModal();
  };

  return (
    <Modal open={openModal} onClose={handleLocalCloseModal} data-testid="user-modal">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          maxHeight: '80vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          {isNewUser ? "Crear Usuario" : "Editar Usuario"}
        </Typography>
        
        {/* Email */}
        <TextField
          fullWidth
          label="Email"
          variant="outlined"
          margin="normal"
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          disabled={!isNewUser}
          error={emailError}
          helperText={emailError ? "Por favor ingresa un email válido" : ""}
        />
        
        {/* Template */}
        <FormControl fullWidth margin="normal" error={templateError}>
          <InputLabel id="template-select-label">Template</InputLabel>
          <Select
            labelId="template-select-label"
            value={template}
            onChange={handleTemplateChange}
            label="Template"
          >
            {Array.isArray(templates) && templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.template_name}
              </MenuItem>
            ))}
          </Select>
          {templateError && (
            <Typography variant="body2" color="error">
              Template es requerido
            </Typography>
          )}
        </FormControl>

        {/* ✨ BIRTHDAY - Date Picker */}
        {features.specialTemplatesEnabled && (
          <TextField
            fullWidth
            type="date"
            label="Fecha de Cumpleaños"
            variant="outlined"
            margin="normal"
            value={birthday}
            onChange={handleBirthdayChange}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Formato: DD/MM/AAAA"
          />
        )}

        {/* ✨ ANIVERSARY - Date Picker */}
        {features.specialTemplatesEnabled && (
          <TextField
            fullWidth
            type="date"
            label="Fecha de Ingreso"
            variant="outlined"
            margin="normal"
            value={aniversary}
            onChange={handleAniversaryChange}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Fecha de inicio en la empresa"
          />
        )}

        {/* Usuario Activo */}
        <FormControlLabel
          style={{ width: "100%" }}
          control={<Switch checked={isActive} onChange={handleActiveChange} />}
          label="Usuario activo"
        />

        {/* Resto de campos dinámicos (sin birthday ni aniversary) */}
        {Array.isArray(dynamicInputs) && dynamicInputs.map((input) => (
          <Box
            key={input.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 2,
              borderBottom: '1px solid #e0e0e0',
              pb: 2,
            }}
          >
            <Box sx={{ flex: 1, pr: 2 }}>
              <Typography variant="subtitle1">{input.name}{input.is_required && '*'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {input.description}
              </Typography>
            </Box>
            <TextField
              value={input.value}
              onChange={(e) => handleInputChange(input.name, e.target.value)}
              variant="outlined"
              size="small"
              sx={{ flex: 2, mr: 2 }}
            />
          </Box>
        ))}

        <Button
          variant="contained"
          onClick={onSave}
          sx={{ mt: 2, mr: 2 }}
          disabled={!isEmailValid || !email || !template}
        >
          Guardar Cambios
        </Button>
        <Button variant="outlined" onClick={handleLocalCloseModal} sx={{ mt: 2 }}>
          Cancelar
        </Button>
      </Box>
    </Modal>
  );
}

export default UserModal;